import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import {usePersonsStore} from '@store/persons'
import {usePhotosStore} from '@store/photos'
import {useCultsStore} from '@pages/CultsPage/store'
import {useTagsStore} from '@pages/TagsPage/store'

import useOnScreen from '@hooks/useOnScreen'
import useHostBasedUrl from '@hooks/useHostBasedUrl'
import useRegetPerson from '@hooks/useRegetPerson'

import {uploadImage} from '@api/requests'

import CustomFields from '@components/molecules/CustomFields'
import ReactQuillEditor from '@components/molecules/ReactQuillEditor'
import ThemedSelect from '@components/atoms/ThemedSelect'

import EditSimCatalogParams from './EditSimCatalogParams'

import {AVATAR_IMAGE_TARGET_SIZES} from '@constants/imageUploading'

import {AvatarModel, CatalogSkill} from '@app/api/models'
import {CatalogCodeName} from '@app/store/useCatalogStore'
import {isTimestamp} from '@app/utils/datetime'
import EditSimSkills from '@_metronic/partials/layout/editsim-drawer/EditSimSkills'
import {useAddEditCultDrawerStore} from '../addcult-drawer/AddEditCultDrawer'

export type TagOptionType = {
  label: string
  value: number
}

export type SimValuesType = {
  first_name: string
  last_name: string
  is_alive: number
  deadReason: string
  species: string
  type: number
  gender: number
  otherGender: string
  genderPronoun: string
  look: number | null
  city: string
  character_traits: string
  life_goals: string
  description: string
  avatar: AvatarModel | null
  selectedTags: TagOptionType[]
}

export const DEFAULT_SIM_VALUES: SimValuesType = {
  first_name: '',
  last_name: '',
  is_alive: 1,
  deadReason: '',
  species: '',
  type: 0,
  gender: 0,
  otherGender: '',
  genderPronoun: '',
  look: null,
  city: '',
  character_traits: '',
  life_goals: '',
  description: '',
  avatar: null,
  selectedTags: [],
}

// TODO
// объединить в общий компонент с AddSimDrawerContent

const EditSimDrawerContent = ({personId, handleValuesChange, editPersonValues}) => {
  const butOpenEditorRef = useRef<any>()

  const {tags: unSortTags, loadTags} = useTagsStore()
  const {selectPhoto} = usePhotosStore()
  const {persons} = usePersonsStore()
  const {cults, loadCults} = useCultsStore()

  const [values, setValues] = useState<SimValuesType>(DEFAULT_SIM_VALUES)
  // useEffect(() => {
  //   console.log('### values', values)
  // }, [values])
  const [customsFields, setCustomFields] = useState<any>([])
  const [catalog, setCatalog] = useState<any>({})
  const [skills, setSkills] = useState<CatalogSkill[]>([])

  const [avatar_id, setAvatarId] = useState<number | null>(null)

  const {open: openAddCultDrawer} = useAddEditCultDrawerStore()

  useEffect(() => {
    loadCults()
  }, [])

  const tags = useMemo(
    () => unSortTags?.map((tag) => ({label: tag.name, value: tag.id})) as TagOptionType[],
    [unSortTags]
  )
  const editSimDrawerRef = useRef(null)
  const visible = useOnScreen(editSimDrawerRef)

  useEffect(() => {
    // console.log('### visible', visible)
    if (!visible) {
      // console.log('### setValues(DEFAULT_SIM_VALUES)#1')
      setValues(DEFAULT_SIM_VALUES)
      setCustomFields([])
      setCatalog({})

      setAvatarId(null)
    } else {
      loadTags()
    }
  }, [visible])

  const intl = useIntl()

  const [loading, setLoading] = useState(false)
  const regetPerson = useRegetPerson(personId)
  useEffect(() => {
    if (personId) {
      // console.log('### regetPerson', personId)
      setLoading(true)
      regetPerson()
        .then((receivedPerson) => {
          if (receivedPerson) {
            console.log('### receivedPerson', receivedPerson)

            setValues((state) => ({
              ...state,
              ...{
                first_name: receivedPerson.first_name || '',
                last_name: receivedPerson.last_name || '',
                is_alive: receivedPerson.is_alive,
                deadReason: receivedPerson.deadReason || '',
                species: receivedPerson.species || '',
                type: receivedPerson.type || DEFAULT_SIM_VALUES.type,
                gender: receivedPerson.gender,
                otherGender: receivedPerson.other_gender,
                genderPronoun: receivedPerson.params?.genderPronoun || '',
                look: receivedPerson.look_id || null,
                city: receivedPerson.city || '',
                character_traits: receivedPerson.character_traits || '',
                life_goals: receivedPerson.life_goals || '',
                description: receivedPerson.description || '',
                avatar: receivedPerson.avatar || null,
                selectedTags: receivedPerson.tags.map(({id, name}) => ({label: name, value: id})),
              },
            }))

            setCustomFields(
              receivedPerson.params && receivedPerson.params.fields
                ? receivedPerson.params.fields.map(([name, value, image, ts]) => [
                    name,
                    value,
                    image ? (isTimestamp(image) ? null : image) : null,
                    ts,
                  ])
                : []
            )
            console.log('### receivedPerson.params', receivedPerson.params)
            setCatalog(receivedPerson.params?.catalog ?? {})
            setSkills(receivedPerson.params?.skills ?? [])
            // @ts-ignore
            setAvatarId(receivedPerson.avatar ? receivedPerson.avatar.id : null)
          }
        })
        .finally(() => setLoading(false))
    }
  }, [personId])

  useEffect(() => {
    // @ts-ignore
    const foundPerson = persons.find((x) => parseInt(x.id) === personId)

    if (foundPerson) {
      const {
        avatar,
        first_name,
        species,
        last_name,
        gender,
        selectedTags,
        otherGender,
        is_alive,
        deadReason,
        type,
        city,
        look,
        character_traits,
        life_goals,
        description,
        genderPronoun,
      } = values

      handleValuesChange({
        avatar,
        first_name,
        species,
        last_name,
        avatar_id,
        gender,
        tags: selectedTags.map(({value}) => value),
        other_gender: otherGender,
        is_alive,
        deadReason,
        type,
        city,
        look_id: look,
        character_traits,
        life_goals,
        description,
        // @ts-ignore
        params: {
          ...foundPerson.params,
          fields: customsFields ? customsFields : [],
          genderPronoun,
          catalog,
          skills,
        },
      })
    }
  }, [values, customsFields, skills, catalog, avatar_id])

  const callBackForCrop = (file) => {
    let reader = new FileReader()
    reader.onload = function (e) {
      if (e && e.target && e.target.result) {
        uploadImage(file, 1).then((response) => {
          setAvatarId(response.data.id)
          // console.log('### callBackForCrop.setValues')
          setValues((state) => ({...state, avatar: response.data}))
        })
      }
    }
    reader.readAsDataURL(file)
  }

const editAvatar = async (event) => {
  if (!avatarUrl) return;
  
  try {
    let blob;
    
    if (avatarUrl.includes('cdn.thesimstree.com')) {
      blob = await loadImageViaCanvas(avatarUrl);
    } else {
      const response = await fetch(avatarUrl, {mode: "no-cors"});
      blob = await response.blob();
    }
    
    if (blob && blob.size > 0) {
      selectPhoto(blob, callBackForCrop, 1, AVATAR_IMAGE_TARGET_SIZES, true, true);
    } else {
      throw new Error('Invalid blob');
    }
    
  } catch (error) {
    console.error('Avatar edit error:', error);
  }
}

  const loadImageViaCanvas = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/png');
    };
    
    img.onerror = reject;
    img.src = url;
  });
}

  function handleFiles(event) {
    let file = event.currentTarget.files[0]
    selectPhoto(file, callBackForCrop, 1, AVATAR_IMAGE_TARGET_SIZES, false, true)

    event.currentTarget.value = ''

    if (butOpenEditorRef && butOpenEditorRef.current) {
      //@ts-ignore
      butOpenEditorRef.current.click()
    }
  }

  const avatarUrl = useHostBasedUrl(values.avatar?.url)

  // console.log('### values', values)
  // console.log('### values.character_traits', values.character_traits)

  return (
    <div
      className='card-body hover-scroll-overlay-y h-400px pt-5'
      id='kt_help_body'
      ref={editSimDrawerRef}
    >
      <button
        ref={butOpenEditorRef}
        style={{pointerEvents: 'none', opacity: 0, position: 'absolute'}}
        data-bs-toggle='modal'
        data-bs-target='#kt_modal_edit_photo'
      />
      {/* begin::Content */}
      <div className='row mb-6'>
        <label className='col-lg-12 col-form-label fw-semibold fs-6'>
          {intl.formatMessage({id: 'USER.PHOTO'})}
        </label>
        <div className='col-lg-12'>
          <div className='image-input image-input-outline' data-kt-image-input='true'>
            <div
              style={{
                backgroundImage: avatarUrl
                  ? //@ts-ignore
                    `url('${avatarUrl}')`
                  : 'url("https://my-engine.ru/modules/users/avatar.png")',
              }}
              className='image-input-wrapper w-125px h-125px'
            />
            <div
              className='d-flex flex-row w-100 justify-content-end column-gap-1'
              style={{left: 0, transform: 'translateX(0) translateY(-10px)'}}
              data-kt-image-input-action='change'
            >
              {avatarUrl && (
                <label
                  className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                  title='Edit avatar'
                  onClick={editAvatar}
                >
                  <i className='ki-duotone ki-setting-2'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                  </i>
                </label>
              )}
              <label
                className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                data-bs-toggle='tooltip'
                title='Change avatar'
              >
                <i className='ki-duotone ki-pencil fs-7'>
                  <span className='path1' />
                  <span className='path2' />
                </i>
                <input
                  onChange={handleFiles}
                  type='file'
                  name='avatar'
                  accept='.png, .jpg, .jpeg'
                />
                <input type='hidden' name='avatar_remove' />
              </label>
            </div>

            <span
              className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
              data-kt-image-input-action='cancel'
              data-bs-toggle='tooltip'
              title='Cancel avatar'
            >
              <i className='ki-duotone ki-cross fs-2'>
                <span className='path1' />
                <span className='path2' />
              </i>
            </span>
          </div>
          <div className='form-text'>{intl.formatMessage({id: 'TREE.ADD.PHOTO.FORMAT'})}</div>
        </div>
      </div>
      <div className='mb-10'>
        <label className='required form-label'>{intl.formatMessage({id: 'USER.FIRST_NAME'})}</label>
        <input
          type='text'
          value={values.first_name}
          className='form-control'
          placeholder=''
          onChange={(e) => {
            // console.log('### onChangesetValues.#1')
            setValues({...values, first_name: e.currentTarget.value})
          }}
        />
      </div>
      <div className='mb-10'>
        <label className='form-label'>{intl.formatMessage({id: 'USER.LAST_NAME'})}</label>
        <input
          type='text'
          value={values.last_name}
          className='form-control'
          placeholder=''
          onChange={(e) => {
            // console.log('### onChangesetValues.#2')
            setValues({...values, last_name: e.currentTarget.value})
          }}
        />
      </div>
      <div className='mb-10'>
        <label className=' form-label'>{intl.formatMessage({id: 'SIM.GENDER'})}</label>
        <select
          value={values.gender}
          className='form-select'
          aria-label='Select example'
          onChange={(e) => {
            // console.log('### onChangesetValues.#3')
            setValues({...values, gender: +e.currentTarget.value})
          }}
        >
          <option>{intl.formatMessage({id: 'SELECT.GENDER'})}</option>
          <option value={0}>{intl.formatMessage({id: 'SELECT.GENDER.MALE'})}</option>
          <option value={1}>{intl.formatMessage({id: 'SELECT.GENDER.FEMALE'})}</option>
          <option value={2}>{intl.formatMessage({id: 'SELECT.GENDER.OTHER'})}</option>
        </select>
      </div>
      {values.gender === 2 ? (
        <>
          <div className='mb-10'>
            <label className=' form-label'>{intl.formatMessage({id: 'SIM.OTHER_GENDER'})}</label>
            <input
              type='text'
              value={values.otherGender}
              className='form-control'
              placeholder={intl.formatMessage({id: 'SIM.GENDER.ENTER'})}
              onChange={(e) => {
                // console.log('### onChangesetValues.#4')
                setValues({...values, otherGender: e.currentTarget.value})
              }}
            />
          </div>
          <div className='mb-10'>
            <label className=' form-label'>
              {intl.formatMessage({id: 'SELECT.GENDER.PRONOUNS'})}
            </label>
            <input
              type='text'
              value={values.genderPronoun}
              className='form-control'
              placeholder=''
              onChange={(e) => {
                // console.log('### onChangesetValues.#5')
                setValues({...values, genderPronoun: e.currentTarget.value})
              }}
            />
          </div>
        </>
      ) : null}
      {/* Поле "Город" */}
      <EditSimCatalogParams
        allSimValues={values}
        values={catalog}
        visibleCodeNames={[CatalogCodeName.location]}
        onChange={setCatalog}
        onAllValuesChange={(updatedValues) => {
          // console.log('### onAllValuesChange', updatedValues)
          setValues(updatedValues)
        }}
      />

      <div className='mb-10'>
        <label className=' form-label'>{intl.formatMessage({id: 'SIM.CONDITION'})}</label>
        <select
          value={values.is_alive}
          className='form-select'
          aria-label='Select example'
          onChange={(e) => {
            // console.log('### onChangesetValues.#6')
            setValues({...values, is_alive: +e.currentTarget.value})
          }}
        >
          <option>{intl.formatMessage({id: 'SIM.CONDITION.SELECT'})}</option>
          <option value={1}>{intl.formatMessage({id: 'SIM.CONDITION.ALIVE'})}</option>
          <option value={0}>{intl.formatMessage({id: 'SIM.CONDITION.DEAD'})}</option>
        </select>
      </div>
      <EditSimCatalogParams
        allSimValues={values}
        values={catalog}
        visibleCodeNames={[CatalogCodeName.causes_of_death]}
        onChange={setCatalog}
        onAllValuesChange={(updatedValues) => {
          // console.log('### onAllValuesChange', updatedValues)
          setValues(updatedValues)
        }}
      />
      <div className='mb-10'>
        <label className=' form-label'>{intl.formatMessage({id: 'SIM.TYPE'})}</label>
        <select
          value={values.type}
          className='form-select'
          aria-label='Select example'
          onChange={(e) => {
            // console.log('### onChangesetValues.#7')
            setValues({...values, type: +e.currentTarget.value})
          }}
        >
          <option>{intl.formatMessage({id: 'SIM.TYPE.SELECT'})}</option>
          <option value={0}>{intl.formatMessage({id: 'SIM.TYPE.PERSON'})}</option>
          <option value={1}>{intl.formatMessage({id: 'SIM.TYPE.PET'})}</option>
        </select>
      </div>
      <div className='mb-10'>
        <label className=' form-label'>{intl.formatMessage({id: 'SIDEBAR.MENU.TAGS'})}</label>
        <ThemedSelect
          placeholder={intl.formatMessage({id: 'SIM.TAG.SELECT'})}
          className='react-select-styled'
          classNamePrefix='react-select'
          isMulti
          options={tags}
          value={values.selectedTags}
          onChange={(selectedTag) => {
            // console.log('### onChangesetValues.#8')
            setValues({...values, selectedTags: selectedTag as TagOptionType[]})
          }}
        />
      </div>
      <div className='mb-10'>
        <label className='form-label'>{intl.formatMessage({id: 'SIDEBAR.MENU.CULT'})}</label>
        <ThemedSelect
          className='react-select-styled'
          classNamePrefix='react-select'
          isFirstElementButton
          isSearchable
          isClearable
          options={[
            {
              value: 'ADD_NEW',
              label: <span>+ {intl.formatMessage({id: 'ADD.OCCULT'})}</span>,
            },
            ...(cults?.map(({id, name}) => ({
              value: id,
              label: name,
            })) || []),
          ]}
          value={
            values.look
              ? {value: values.look, label: cults?.find((c) => c.id === values.look)?.name || ''}
              : null
          }
          onChange={(option: any) => {
            if (option?.value === 'ADD_NEW') {
              openAddCultDrawer()
            } else if (option) {
              setValues({...values, look: option.value})
            } else {
              setValues({...values, look: null})
            }
          }}
          placeholder={intl.formatMessage({id: 'SIM.CULT.SELECT'})}
        />
      </div>

      <EditSimCatalogParams
        allSimValues={values}
        values={catalog}
        hiddenCodeNames={[CatalogCodeName.causes_of_death, CatalogCodeName.location]}
        onChange={setCatalog}
        onAllValuesChange={(updatedValues) => {
          // console.log('### onAllValuesChange', updatedValues)
          setValues(updatedValues)
        }}
      />

      {values.type === 0 && <EditSimSkills activeSkills={skills} onChange={setSkills} />}

      <CustomFields allowCopy values={customsFields} onChange={setCustomFields} />

      {visible && !loading ? (
        <div className='mb-10'>
          <label className='form-label'>{intl.formatMessage({id: 'TREE.ADD.DESCRIPTION'})}</label>
          <ReactQuillEditor
            initValue={values.description}
            setValue={(value) => {
              // console.log('### onChangesetValues.#10', `${values.description}`, '->', `${value}`)
              if (value !== values.description) {
                setValues({...values, description: value})
              }
            }}
          />
        </div>
      ) : null}

      {/* end::Content */}
    </div>
  )
}

export {EditSimDrawerContent}
